import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { HeraldPreviousConversation } from "@/data-access/herald-chat/chat-queries";
import { createHeraldChatHistoryActions } from "./herald-chat-history.actions";

export type { HeraldPreviousConversation };

export interface OptimisticConversationInput {
  id: string;
  title?: string | null;
}

/** `title` is the row label at open time (normalized in the store). */
export type ConversationModalSetState =
  | { open: true; conversationId: string; title?: string }
  | { open: false };

export interface SetRenameConversationTargetInput {
  targetConversationId: string;
  titleToBeRenamed: string;
}

export interface HeraldChatHistoryModalState {
  targetConversationId: string | null;
  /** Normalized row title captured when opening rename or delete (for UI / toasts). */
  targetConversationTitle: string;
  titleToBeRenamed: string;
  isRenameConversationModalOpen: boolean;
  isDeleteConversationModalOpen: boolean;
  isDeletingConversation: boolean;
}

export const heraldChatHistoryModalInitialState: HeraldChatHistoryModalState = {
  targetConversationId: null,
  targetConversationTitle: "",
  titleToBeRenamed: "",
  isRenameConversationModalOpen: false,
  isDeleteConversationModalOpen: false,
  isDeletingConversation: false,
};

export interface HeraldChatHistoryConversationsState {
  allPreviousConversations: HeraldPreviousConversation[];
  isFetchingConversations: boolean;
  isFetchingMoreConversations: boolean;
  conversationsFetchError: string | null;
  /** Server total from the latest conversations query. */
  conversationsTotalCount: number | null;
  /** Next offset for {@link HeraldChatHistorySlice.fetchMorePreviousConversations}. */
  conversationsNextOffset: number;
  hasMoreConversations: boolean;
  /** Pauses message fetch for this id after first-stream hydration. */
  skipMessagesFetchForConversationId: string | null;
}

export const heraldChatHistoryConversationsInitialState: HeraldChatHistoryConversationsState =
  {
    allPreviousConversations: [],
    isFetchingConversations: false,
    isFetchingMoreConversations: false,
    conversationsFetchError: null,
    conversationsTotalCount: null,
    conversationsNextOffset: 0,
    hasMoreConversations: false,
    skipMessagesFetchForConversationId: null,
  };

export interface FetchConversationsOptions {
  /** Bypass urql cache (e.g. after active thread changes or window focus). */
  requestPolicy?: "network-only";
  /** Chat URL active id (`null` = `new`); drives optimistic-row merge for this fetch. */
  activeConversationId?: string | null;
}

export interface HeraldChatHistorySlice
  extends HeraldChatHistoryModalState,
    HeraldChatHistoryConversationsState {
  setRenameConversationTarget: (
    input: SetRenameConversationTargetInput,
  ) => void;
  setTitleToBeRenamed: (title: string) => void;
  setRenameConversationModal: (next: ConversationModalSetState) => void;
  setDeleteConversationModal: (next: ConversationModalSetState) => void;
  resetConversationModalState: () => void;
  fetchAllPreviousConversations: (
    options?: FetchConversationsOptions,
  ) => Promise<void>;
  fetchMorePreviousConversations: () => Promise<void>;
  optimisticallyUpsertConversationAtTop: (
    input: OptimisticConversationInput,
  ) => void;
  /**
   * After the first stream completes: skip reloading messages we already have, prepend the sidebar row.
   */
  registerConversationFromFirstStream: (input: {
    conversationId: string;
    title?: string | null;
  }) => void;
  /**
   * Clears the first-stream message-fetch skip when the active thread id differs (e.g. opened another chat).
   * Invoked from `useEffect`, so one render may still see the prior skip; it only applies when it matches the active id.
   */
  clearSkipMessagesFetchUnlessViewing: (
    activeConversationId: string | undefined,
  ) => void;
  removeConversationFromList: (conversationId: string) => void;
  replacePendingConversationWithServerId: (
    pendingConversationId: string,
    input: OptimisticConversationInput,
  ) => void;
  /**
   * Persists `titleToBeRenamed` for `targetConversationId` when the rename modal is open.
   * Uses `targetConversationTitle` for the success toast “from” label (snapshot at open).
   */
  renameConversation: () => Promise<void>;
  /** Deletes the conversation targeted by the delete modal (`targetConversationId`). */
  deleteConversation: () => Promise<boolean>;
}

const createHeraldChatHistorySlice: StateCreator<
  HeraldChatHistorySlice,
  [["zustand/devtools", never]],
  [],
  HeraldChatHistorySlice
> = (set, get, api) => ({
  ...heraldChatHistoryConversationsInitialState,
  ...heraldChatHistoryModalInitialState,

  ...createHeraldChatHistoryActions(set, get, api),
});

const useHeraldChatHistoryStore = create<HeraldChatHistorySlice>()(
  devtools(createHeraldChatHistorySlice, {
    name: "herald-chat-history-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useHeraldChatHistoryStore;
